import React, { useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { rapportService } from '../../api/services';
import { RapportTVAData } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

export function RapportTVA() {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [annee, setAnnee] = useState(currentYear);
  const [mois, setMois] = useState(currentMonth);
  const [data, setData] = useState<RapportTVAData | null>(null);
  const [loading, setLoading] = useState(false);

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const result = await rapportService.getTVA(annee, mois);
      setData(result);
    } catch (error) {
      toast.error(t.messages.loadError);
      setData({
        periode: `${t.months[mois - 1]} ${annee}`,
        tva7Collectee: 0,
        tva13Collectee: 0,
        tva19Collectee: 0,
        totalTvaCollectee: 0,
        baseHT7: 0,
        baseHT13: 0,
        baseHT19: 0,
        totalBaseHT: 0,
        fodecTotal: 0,
        timbreTotal: 0,
        tvaDeductible: 0,
        tvaNetteAPayer: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t.rapports.tva.title}</h1>
          <p className="page-subtitle">
            Déclaration mensuelle de la TVA collectée
          </p>
        </div>
        {data && (
          <button onClick={handlePrint} className="btn-outline no-print">
            <PrinterIcon className="h-4 w-4 mr-1" />
            {t.common.print}
          </button>
        )}
      </div>

      <div className="card no-print">
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div>
            <label className="label">{t.rapports.tva.mois}</label>
            <select
              value={mois}
              onChange={(e) => setMois(parseInt(e.target.value))}
              className="input-field w-40"
            >
              {t.months.map((month, idx) => (
                <option key={idx} value={idx + 1}>{month}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">{t.rapports.tva.annee}</label>
            <select
              value={annee}
              onChange={(e) => setAnnee(parseInt(e.target.value))}
              className="input-field w-32"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button onClick={handleGenerate} className="btn-primary">
            {t.rapports.tva.generer}
          </button>
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {data && !loading && (
        <div className="space-y-6">
          <div className="card">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {t.rapports.tva.title}
              </h2>
              <p className="text-gray-600 mt-1">{t.rapports.tva.periode}: {data.periode}</p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t.rapports.tva.tvaCollectee}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      {t.rapports.tva.baseHT}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      {t.rapports.tva.tvaCollectee}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {t.rapports.tva.tva7}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <MoneyDisplay amount={data.baseHT7} size="sm" />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <MoneyDisplay amount={data.tva7Collectee} size="sm" />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {t.rapports.tva.tva13}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <MoneyDisplay amount={data.baseHT13} size="sm" />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <MoneyDisplay amount={data.tva13Collectee} size="sm" />
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {t.rapports.tva.tva19}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <MoneyDisplay amount={data.baseHT19} size="sm" />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <MoneyDisplay amount={data.tva19Collectee} size="sm" />
                    </td>
                  </tr>
                  <tr className="bg-primary-50 font-bold">
                    <td className="px-6 py-4 text-sm text-primary-900">
                      {t.rapports.tva.totalTva}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <MoneyDisplay amount={data.totalBaseHT} size="sm" className="font-bold" />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <MoneyDisplay amount={data.totalTvaCollectee} size="sm" className="font-bold text-primary-700" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">{t.rapports.tva.fodec}</p>
                  <MoneyDisplay amount={data.fodecTotal} size="lg" />
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">{t.rapports.tva.timbre}</p>
                  <MoneyDisplay amount={data.timbreTotal} size="lg" />
                </div>
                <div className="bg-primary-50 rounded-lg p-4">
                  <p className="text-sm text-primary-600 font-medium">TVA collectée</p>
                  <MoneyDisplay amount={data.totalTvaCollectee} size="lg" className="text-primary-700" />
                </div>
              </div>
            </div>

            {/* Liquidation de la TVA : collectée − déductible = nette à payer (ou crédit). */}
            <div className="mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">TVA collectée (ventes)</p>
                  <MoneyDisplay amount={data.totalTvaCollectee} size="lg" />
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">TVA déductible (achats)</p>
                  <MoneyDisplay amount={data.tvaDeductible} size="lg" />
                </div>
                <div className={`rounded-lg p-4 ${data.tvaNetteAPayer >= 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                  <p className={`text-sm font-medium ${data.tvaNetteAPayer >= 0 ? 'text-amber-700' : 'text-green-700'}`}>
                    {data.tvaNetteAPayer >= 0 ? 'TVA nette à payer' : 'Crédit de TVA'}
                  </p>
                  <MoneyDisplay amount={Math.abs(data.tvaNetteAPayer)} size="lg"
                    className={data.tvaNetteAPayer >= 0 ? 'text-amber-700' : 'text-green-700'} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RapportTVA;
